import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { format, isWithinInterval, parseISO } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Calendar,
  Plus,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Pencil,
  Trash
} from "lucide-react";
import { clientApi, Client, financialApi, FinancialRecord } from "@/services/api";
import { useNavigate } from "react-router-dom";

const Finance = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showAddRecordDialog, setShowAddRecordDialog] = useState(false);
  const [recordType, setRecordType] = useState<"income" | "expense">("income");
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [recordDate, setRecordDate] = useState("");
  const [recordAmount, setRecordAmount] = useState("");
  const [recordDescription, setRecordDescription] = useState("");
  const [recordCategory, setRecordCategory] = useState("");
  const [recordOutstandingBalance, setRecordOutstandingBalance] = useState("");
  const [recordRemainingBalance, setRecordRemainingBalance] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<FinancialRecord | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  // Calculate remaining balance when outstanding balance or amount changes
  useEffect(() => {
    if (recordOutstandingBalance && recordAmount) {
      const outstanding = parseFloat(recordOutstandingBalance);
      const amount = parseFloat(recordAmount);
      if (!isNaN(outstanding) && !isNaN(amount)) {
        const remaining = Math.max(0, outstanding - amount);
        setRecordRemainingBalance(remaining.toString());
      }
    } else {
      setRecordRemainingBalance("");
    }
  }, [recordOutstandingBalance, recordAmount]);

  const loadRecords = async () => {
    const recordsRes = await financialApi.getAll();
    setRecords(Array.isArray(recordsRes) ? recordsRes : []);
  };

  useEffect(() => {
    setLoading(true);
    loadRecords()
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
    clientApi.getAll()
      .then(setClients)
      .catch(() => setClients([]));
  }, []);

  // Filter records based on selected date range (include the full end day)
  const filteredRecords = startDate && endDate
    ? records.filter(record => {
      const recordDate = new Date(record.date);
      const start = parseISO(startDate);
      start.setHours(0, 0, 0, 0);
      const end = parseISO(endDate);
      end.setHours(23, 59, 59, 999);
      return isWithinInterval(recordDate, { start, end });
    })
    : records;

  // Calculate totals
  const totalIncome = filteredRecords
    .filter(record => record.type === "income")
    .reduce((sum, record) => sum + record.amount, 0);

  const totalExpenses = filteredRecords
    .filter(record => record.type === "expense")
    .reduce((sum, record) => sum + record.amount, 0);

  const netProfit = totalIncome - totalExpenses;

  const handleAddRecord = () => {
    // reset form for creating a new record
    setEditingRecordId(null);
    setRecordType("income");
    setRecordDate("");
    setRecordAmount("");
    setRecordDescription("");
    setRecordCategory("");
    setSelectedClientId("");
    setRecordOutstandingBalance("");
    setRecordRemainingBalance("");
    setShowAddRecordDialog(true);
  };

  // The API populates `client` as an object ({ _id, name, ... }), but it may
  // also be a plain ID string. Normalize it to the ID string.
  const getRecordClientId = (record: FinancialRecord): string => {
    const c: any = record.client;
    if (!c) return "";
    return typeof c === "object" ? (c._id || "") : c;
  };

  const handleEditRecord = (record: FinancialRecord) => {
    setEditingRecordId(record._id || null);
    setRecordType(record.type);
    try {
      const dateInput = format(new Date(record.date), "yyyy-MM-dd");
      setRecordDate(dateInput);
    } catch {
      setRecordDate("");
    }
    setRecordAmount(String(record.amount ?? ""));
    setRecordDescription(record.description || "");
    setRecordCategory(record.category || "");
    setSelectedClientId(getRecordClientId(record));
    setRecordOutstandingBalance(
      record.totalAmount !== undefined ? String(record.totalAmount) : ""
    );
    setRecordRemainingBalance(
      record.remainingBalance !== undefined ? String(record.remainingBalance) : ""
    );
    setShowAddRecordDialog(true);
  };

  const handleDeleteRecord = (record: FinancialRecord) => {
    if (!record._id) return;
    setRecordToDelete(record);
  };

  const confirmDeleteRecord = async () => {
    const id = recordToDelete?._id;
    if (!id) return;
    setDeleting(true);
    try {
      await financialApi.delete(id);
      setRecords(prev => prev.filter(r => r._id !== id));
      toast({
        title: "Record Deleted",
        description: "The financial record has been removed.",
        variant: "default",
      });
      setRecordToDelete(null);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete record.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveRecord = async () => {
    try {
      if (!recordDate || !recordAmount || !recordDescription || !recordCategory) {
        toast({
          title: "Missing Fields",
          description: "Please fill in all required fields.",
          variant: "destructive",
        });
        return;
      }

      // Find the selected client to get the name
      const selectedClient = selectedClientId ? clients.find(c => c._id === selectedClientId) : null;

      const payload = {
        date: new Date(recordDate),
        type: recordType,
        amount: parseFloat(recordAmount),
        description: recordDescription,
        category: recordCategory,
        client: selectedClientId ? selectedClientId : undefined,
        clientName: selectedClient ? selectedClient.name : null,
        totalAmount: recordType === "income" && recordOutstandingBalance ? parseFloat(recordOutstandingBalance) : undefined,
        remainingBalance: recordType === "income" && recordRemainingBalance ? parseFloat(recordRemainingBalance) : undefined,
      };

      if (editingRecordId) {
        await financialApi.update(editingRecordId, payload);
        await loadRecords();
        setShowAddRecordDialog(false);
        toast({
          title: "Record Updated",
          description: `The ${recordType} record has been updated successfully.`,
          variant: "default",
        });
      } else {
        await financialApi.create(payload);
        await loadRecords();
        setShowAddRecordDialog(false);
        // Only on create: If this is an income record with a client, set or update their outstanding balance
        if (recordType === "income" && selectedClientId) {
          if (recordOutstandingBalance) {
            const outstanding = parseFloat(recordOutstandingBalance);
            const amount = parseFloat(recordAmount);
            if (!isNaN(outstanding) && !isNaN(amount)) {
              const remaining = Math.max(0, outstanding - amount);
              await clientApi.update(selectedClientId, {
                outstandingBalance: remaining
              });
            }
          } else {
            const amount = parseFloat(recordAmount);
            if (!isNaN(amount)) {
              await clientApi.update(selectedClientId, {
                outstandingBalance: amount
              });
            }
          }
        }
        toast({
          title: "Record Added",
          description: `New ${recordType} record has been added successfully.`,
          variant: "default",
        });
      }

      // Reset form
      setRecordDate("");
      setRecordAmount("");
      setRecordDescription("");
      setRecordCategory("");
      setSelectedClientId("");
      setRecordOutstandingBalance("");
      setRecordRemainingBalance("");
      setEditingRecordId(null);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || (editingRecordId ? "Failed to update record." : "Failed to add record."),
        variant: "destructive",
      });
    }
  };

  // Helper function to get client name for a record
  const getClientName = (record: FinancialRecord) => {
    // First, try to get the client name from the record itself (if saved)
    if ((record as any).clientName) {
      return (record as any).clientName;
    }

    // Then try the populated client object returned by the API
    const populated: any = record.client;
    if (populated && typeof populated === "object" && populated.name) {
      return populated.name;
    }

    // If not found, try to find the client by ID
    const clientId = getRecordClientId(record);
    if (clientId) {
      const client = clients.find(c => c._id === clientId);
      return client ? client.name : "Unknown Client";
    }

    // No client associated
    return "-";
  };

  // Card click handlers for filtering
  const showIncome = () => {
    setStartDate("");
    setEndDate("");
    // Optionally, set a filter state to only show income
  };
  const showExpenses = () => {
    setStartDate("");
    setEndDate("");
    // Optionally, set a filter state to only show expenses
  };
  const showNetProfit = () => {
    setStartDate("");
    setEndDate("");
    // Optionally, set a filter state to show all
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Financial Records</h1>
          <p className="text-muted-foreground">
            Track income, expenses, and outstanding balances
          </p>
        </div>
        <Button onClick={handleAddRecord} className="transition-all hover:scale-105 active:scale-95">
          <Plus className="mr-2 h-4 w-4" /> Add Record
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card onClick={showIncome} className="cursor-pointer hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Total Income</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{totalIncome}</div>
            <p className="text-xs text-muted-foreground">
              {startDate && endDate ? `${format(parseISO(startDate), "MMM d, yyyy")} - ${format(parseISO(endDate), "MMM d, yyyy")}` : "All time"}
            </p>
          </CardContent>
        </Card>
        <Card onClick={showExpenses} className="cursor-pointer hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{totalExpenses}</div>
            <p className="text-xs text-muted-foreground">
              {startDate && endDate ? `${format(parseISO(startDate), "MMM d, yyyy")} - ${format(parseISO(endDate), "MMM d, yyyy")}` : "All time"}
            </p>
          </CardContent>
        </Card>
        <Card onClick={showNetProfit} className="cursor-pointer hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
            {netProfit >= 0 ? (
              <ArrowUpRight className="h-4 w-4 text-green-500" />
            ) : (
              <ArrowDownRight className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {Math.abs(netProfit)}
            </div>
            <p className="text-xs text-muted-foreground">
              {startDate && endDate ? `${format(parseISO(startDate), "MMM d, yyyy")} - ${format(parseISO(endDate), "MMM d, yyyy")}` : "All time"}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Financial Ledger</CardTitle>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 mt-4">
            <div className="grid gap-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
              className="h-10 mt-2 sm:mt-0"
            >
              Clear
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <>
            <p className="text-sm text-muted-foreground mb-3">
              Showing {filteredRecords.length} {filteredRecords.length === 1 ? 'record' : 'records'}
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((record) => {
                  const clientName = getClientName(record);
                  const recordClientId = getRecordClientId(record);
                  const client = recordClientId ? clients.find(c => c._id === recordClientId) : null;

                  // Use stored values from database or calculate if not available
                  const getTotalAndRemaining = (record: FinancialRecord) => {
                    if (!recordClientId || record.type !== "income") return { total: null, remaining: null };

                    // If we have stored values in the database, use them
                    if (record.totalAmount !== undefined && record.remainingBalance !== undefined) {
                      return { total: record.totalAmount, remaining: record.remainingBalance };
                    }

                    // Fallback calculation for existing records without stored values
                    const clientData = clients.find(c => c._id === recordClientId);
                    if (!clientData) return { total: null, remaining: null };

                    const currentOutstanding = clientData.outstandingBalance || 0;
                    const paymentAmount = record.amount;

                    if (currentOutstanding === 0) {
                      const total = paymentAmount;
                      const remaining = 0;
                      return { total, remaining };
                    } else {
                      const total = currentOutstanding + paymentAmount;
                      const remaining = currentOutstanding;
                      return { total, remaining };
                    }
                  };

                  const { total, remaining } = getTotalAndRemaining(record);

                  return (
                    <TableRow key={record._id}>
                      <TableCell>{format(new Date(record.date), "MMM d, yyyy")}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {record.type === "income" ? (
                            <ArrowUpRight className="h-4 w-4 text-green-500" />
                          ) : (
                            <ArrowDownRight className="h-4 w-4 text-red-500" />
                          )}
                          <span className="capitalize">{record.type}</span>
                        </div>
                      </TableCell>
                      <TableCell>{record.description}</TableCell>
                      <TableCell>{record.category}</TableCell>
                      <TableCell>
                        {client ? (
                          <span
                            className="text-primary underline cursor-pointer hover:text-primary-700"
                            onClick={() => navigate(`/LODYClients/${client._id}`)}
                          >
                            {clientName}
                          </span>
                        ) : clientName !== "-" ? (
                          <span>{clientName}</span>
                        ) : (
                          <span>-</span>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${record.type === "income" ? "text-green-600" : "text-red-600"
                        }`}>
                        {record.amount}
                      </TableCell>
                      <TableCell className="text-right">
                        {total !== null ? (
                          <span className="font-medium text-blue-600">
                            {total}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {remaining !== null ? (
                          <span className={`font-medium ${remaining > 0 ? 'text-orange-600' : 'text-green-600'
                            }`}>
                            {remaining}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEditRecord(record)}>
                            <Pencil className="mr-2 h-3 w-3" /> Edit
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDeleteRecord(record)}>
                            <Trash className="mr-2 h-3 w-3" /> Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddRecordDialog} onOpenChange={setShowAddRecordDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingRecordId ? "Edit Financial Record" : "Add Financial Record"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="record-type" className="text-right">
                Type
              </Label>
              <Select value={recordType} onValueChange={(value: "income" | "expense") => setRecordType(value)}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="record-date" className="text-right">
                Date
              </Label>
              <Input
                id="record-date"
                type="date"
                className="col-span-3"
                value={recordDate}
                onChange={e => setRecordDate(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="record-amount" className="text-right">
                Amount
              </Label>
              <Input
                id="record-amount"
                type="number"
                className="col-span-3"
                placeholder="0.00"
                value={recordAmount}
                onChange={e => setRecordAmount(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="record-description" className="text-right">
                Description
              </Label>
              <Input
                id="record-description"
                className="col-span-3"
                placeholder="Enter description"
                value={recordDescription}
                onChange={e => setRecordDescription(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="record-category" className="text-right">
                Category
              </Label>
              <Input
                id="record-category"
                className="col-span-3"
                placeholder="Enter category"
                value={recordCategory}
                onChange={e => setRecordCategory(e.target.value)}
              />
            </div>
            {recordType === "income" && (
              <>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="record-client" className="text-right">
                    Client
                  </Label>
                  <Select value={selectedClientId} onValueChange={setSelectedClientId}>
                    <SelectTrigger className="col-span-3">
                      <SelectValue placeholder="Select client (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client._id} value={client._id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="record-outstanding" className="text-right">
                    Outstanding Balance
                  </Label>
                  <Input
                    id="record-outstanding"
                    type="number"
                    className="col-span-3"
                    placeholder="Total amount owed by client (optional)"
                    value={recordOutstandingBalance}
                    onChange={e => setRecordOutstandingBalance(e.target.value)}
                  />
                  <div className="col-span-4 text-xs text-muted-foreground">
                    Leave empty if this is the first payment. System will set outstanding balance to payment amount.
                  </div>
                </div>

                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="record-remaining" className="text-right">
                    Remaining After Payment
                  </Label>
                  <Input
                    id="record-remaining"
                    type="number"
                    className="col-span-3 bg-gray-50"
                    placeholder="Auto-calculated"
                    value={recordRemainingBalance}
                    readOnly
                  />
                  <div className="col-span-4 text-xs text-muted-foreground">
                    This shows how much the client will still owe after this payment
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddRecordDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRecord} className="transition-all hover:scale-105 active:scale-95">
              {editingRecordId ? "Update Record" : "Save Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!recordToDelete}
        onOpenChange={(open) => {
          if (!open) setRecordToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete financial record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this {recordToDelete?.type ?? "financial"} record
              {recordToDelete?.description ? ` ("${recordToDelete.description}")` : ""}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteRecord();
              }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Finance;